import { collectSkeletonTargets } from "./measureSkeletonBlocks";
import { getRevealDelayMs, type SkeletonRect } from "./skeletonLayout";

/** Marks a content element that reveals on its own, delayed by `--skeleton-reveal-delay`. */
export const SKELETON_REVEAL_ITEM_ATTRIBUTE = "data-skeleton-reveal-item";
/**
 * Marks a content element outside the viewport when the reveal starts. It skips
 * its own animation and only fades in with the content, so off-screen elements
 * don't each get a compositor layer and a blur.
 */
export const SKELETON_REVEAL_SKIP_ATTRIBUTE = "data-skeleton-reveal-skip";
const REVEAL_DELAY_PROPERTY = "--skeleton-reveal-delay";

type RevealStagger = {
    cleanup: () => void;
    /** Latest reveal delay among elements in the viewport. */
    visibleDelayMs: number;
};

/**
 * Tags the real content's elements in the viewport with the draw delay of
 * their nearest outline, so each one blurs in as its own outline fades.
 */
export function applyRevealStagger(
    content: HTMLElement,
    origin: DOMRect,
    blocks: readonly SkeletonRect[],
    stepMs: number,
): RevealStagger {
    const targets = collectSkeletonTargets(content).filter(
        (target): target is HTMLElement | SVGElement =>
            target instanceof HTMLElement || target instanceof SVGElement,
    );
    let visibleDelayMs = 0;

    for (const target of targets) {
        const rect = target.getBoundingClientRect();

        if (!isInViewport(rect)) {
            target.setAttribute(SKELETON_REVEAL_SKIP_ATTRIBUTE, "");
            continue;
        }

        const delayMs = getRevealDelayMs(
            {
                x: rect.left - origin.left,
                y: rect.top - origin.top,
                width: rect.width,
                height: rect.height,
            },
            blocks,
            stepMs,
        );

        visibleDelayMs = Math.max(visibleDelayMs, delayMs);
        target.style.setProperty(REVEAL_DELAY_PROPERTY, `${delayMs}ms`);
        target.setAttribute(SKELETON_REVEAL_ITEM_ATTRIBUTE, "");
    }

    return {
        cleanup: () => {
            for (const target of targets) {
                target.style.removeProperty(REVEAL_DELAY_PROPERTY);
                target.removeAttribute(SKELETON_REVEAL_ITEM_ATTRIBUTE);
                target.removeAttribute(SKELETON_REVEAL_SKIP_ATTRIBUTE);
            }
        },
        visibleDelayMs,
    };
}

/** Both axes, so slides scrolled past the edge of a carousel count as off-screen too. */
function isInViewport(rect: DOMRect): boolean {
    return (
        rect.bottom > 0 &&
        rect.top < window.innerHeight &&
        rect.right > 0 &&
        rect.left < window.innerWidth
    );
}
